import { Star } from 'lucide-react';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { CompanyMilestone } from '../../../types';
import './TimelineSection.scss';

export interface TimelineSectionProps {
  milestones: CompanyMilestone[];
  loading: boolean;
}

function groupByYear(items: CompanyMilestone[]): Array<[string, CompanyMilestone[]]> {
  const map = new Map<number, CompanyMilestone[]>();
  for (const m of items) {
    if (!map.has(m.year)) map.set(m.year, []);
    map.get(m.year)!.push(m);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([y, list]) => [String(y), list]);
}

export default function TimelineSection({ milestones, loading }: TimelineSectionProps) {
  const { t } = useLang();
  const groups = groupByYear(milestones);
  const showSkeleton = loading && groups.length === 0;
  const empty = !loading && groups.length === 0;

  return (
    <section className="section section--tight about-timeline">
      <div className="container">
        <div className="section-header about-timeline__head">
          <span className="eyebrow">{t('about.history.t')}</span>
          <h2 className="about-timeline__title">{t('about.history.sub')}</h2>
        </div>

        {empty ? (
          <EmptyState title={t('about.empty')} />
        ) : showSkeleton ? (
          <div className="about-timeline__list">
            {[0, 1].map((i) => (
              <div key={i} className="about-timeline__group">
                <Skeleton width={120} height={64} />
                <div className="about-timeline__items">
                  <Skeleton width="100%" height={88} />
                  <Skeleton width="100%" height={88} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="about-timeline__list">
            <div className="about-timeline__rail" aria-hidden="true" />
            {groups.map(([year, items]) => (
              <div key={year} className="about-timeline__group">
                <div className="about-timeline__year">{year}</div>
                <div className="about-timeline__items">
                  {items.map((m) => (
                    <article
                      key={m.id}
                      className={
                        'card-frame about-timeline__item' +
                        (m.is_highlight ? ' about-timeline__item--hl' : '')
                      }
                    >
                      <div className="about-timeline__item-head">
                        <h3 className="about-timeline__item-title">{m.title}</h3>
                        {m.is_highlight ? (
                          <span className="about-timeline__chip">
                            <Star size={12} />
                            <span>{t('about.highlight')}</span>
                          </span>
                        ) : null}
                      </div>
                      {m.description ? (
                        <p className="about-timeline__item-text">{m.description}</p>
                      ) : null}
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
